import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, inject, OnChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

export type TenantPointGrantResultState = 'success' | 'error' | 'processing';

export interface TenantPointGrantTenant {
  id?: string;
  name?: string;
  type?: string;
  accountId?: string;
  code?: string;
}

const POINT_GRANT_REASON_CODE_MAP: Record<string, string> = {
  CustomerCompensation: 'CustomerCompensation',
  CustomerCare: 'CustomerCompensation',
  ServiceCare: 'ServiceCare',
  ServiceCompensation: 'ServiceCare',
  UserRetention: 'UserRetention',
  UserCare: 'UserRetention',
  SystemOutageCompensation: 'SystemOutageCompensation',
  SystemCompensation: 'SystemOutageCompensation',
  OperationsIncentive: 'OperationsIncentive',
  OperationReward: 'OperationsIncentive',
  Other: 'Other'
};

@Component({
  selector: 'app-tenant-point-grant-drawer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzDrawerModule,
    NzFormModule,
    NzIconModule,
    NzInputModule,
    NzSelectModule,
    NzSpinModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-point-grant-drawer.component.html',
  styleUrl: './tenant-point-grant-drawer.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantPointGrantDrawerComponent implements OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  @Input() visible = false;
  @Input() tenant: TenantPointGrantTenant | null = null;

  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly completed = new EventEmitter<void>();

  readonly reasonOptions = [
    { value: 'CustomerCompensation', labelKey: 'tenant.point-grant.reason.customer-compensation' },
    { value: 'ServiceCare', labelKey: 'tenant.point-grant.reason.service-compensation' },
    { value: 'UserRetention', labelKey: 'tenant.point-grant.reason.user-care' },
    { value: 'SystemOutageCompensation', labelKey: 'tenant.point-grant.reason.system-compensation' },
    { value: 'OperationsIncentive', labelKey: 'tenant.point-grant.reason.operation-reward' },
    { value: 'Other', labelKey: 'tenant.point-grant.reason.other' }
  ];

  readonly reasonForm = this.fb.group({
    amount: [null as number | null, [Validators.required, Validators.min(1)]],
    reason: ['', [Validators.required]],
    remark: ['', [Validators.maxLength(200)]]
  });

  currentBalance: number | null = null;
  preparedTenantName = '';
  preparedOpenId = '';
  pointAuthorized: boolean | null = null;
  loadingBalance = false;
  submitting = false;
  confirmingSubmit = false;
  resultState: TenantPointGrantResultState | null = null;
  resultMessage = '';
  resultTraceId = '';

  private lastTenantId = '';

  constructor() {
    this.reasonForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.confirmingSubmit && !this.submitting && !this.resultState) {
        this.confirmingSubmit = false;
        this.cdr.markForCheck();
      }
    });
  }

  ngOnChanges(): void {
    const tenantId = String(this.tenant?.id ?? '').trim();
    if (!this.visible || !tenantId) {
      if (!this.visible) {
        this.lastTenantId = '';
        this.resetTransientState();
      }
      return;
    }

    if (tenantId !== this.lastTenantId) {
      this.lastTenantId = tenantId;
      this.resetFormState();
      this.loadBalance();
    }
  }

  isRemarkRequired(): boolean {
    return String(this.reasonForm.controls.reason.value ?? '').trim() === 'Other';
  }

  canSubmit(): boolean {
    const amount = Number(this.reasonForm.controls.amount.value);
    const reason = String(this.reasonForm.controls.reason.value ?? '').trim();
    const remark = String(this.reasonForm.controls.remark.value ?? '').trim();
    return Number.isInteger(amount) && amount > 0 && !!reason && (!this.isRemarkRequired() || !!remark) && !this.submitting;
  }

  close(): void {
    if (this.submitting) {
      return;
    }
    this.closed.emit();
  }

  submit(): void {
    if (this.submitting || this.resultState) {
      return;
    }

    if (!this.canSubmit()) {
      this.reasonForm.markAllAsTouched();
      this.message.warning(this.t('tenant.point-grant.validation.invalid'));
      return;
    }
    this.confirmingSubmit = true;
    this.cdr.markForCheck();
  }

  get primaryButtonText(): string {
    if (this.resultState) {
      return this.t('common.close');
    }
    return this.confirmingSubmit ? this.t('common.confirm') : this.t('tenant.point-grant.submit');
  }

  get drawerTitle(): string {
    return this.t('tenant.point-grant.title');
  }

  get drawerSubtitle(): string {
    return this.t('tenant.point-grant.subtitle');
  }

  get resultTitle(): string {
    if (this.resultState === 'success') return this.t('tenant.point-grant.result.success');
    if (this.resultState === 'processing') return this.t('tenant.point-grant.result.processing');
    return this.t('tenant.point-grant.result.error');
  }

  get confirmSummary(): string {
    return this.buildDefaultConfirmSummary();
  }

  get confirmTitle(): string {
    return this.t('tenant.point-grant.confirm.title');
  }

  get confirmHints(): string[] {
    return [];
  }

  get confirmTraceId(): string {
    return '';
  }

  get displayTenantName(): string {
    return this.preparedTenantName || this.tenant?.name || '-';
  }

  get displayOpenId(): string {
    return this.preparedOpenId || this.tenant?.accountId || '-';
  }

  onPrimaryAction(): void {
    if (this.resultState) {
      this.close();
      return;
    }
    if (this.confirmingSubmit) {
      this.submitPointGrant();
      return;
    }
    this.submit();
  }

  onAmountInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const rawValue = String(target?.value ?? '');
    const digitsOnly = rawValue.replace(/\D+/g, '').replace(/^0+(?=\d)/, '');
    const normalizedValue = digitsOnly ? Number(digitsOnly) : null;
    this.reasonForm.controls.amount.setValue(normalizedValue, { emitEvent: true });
    if (target && target.value !== digitsOnly) {
      target.value = digitsOnly;
    }
  }

  cancelConfirm(): void {
    if (this.submitting || !this.confirmingSubmit) {
      return;
    }
    this.confirmingSubmit = false;
    this.cdr.markForCheck();
  }

  formatBalance(value: number | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return this.t('common.none');
    }
    const locale = this.i18n.currentLang === 'en-US' ? 'en-US' : this.i18n.currentLang === 'zh-TW' ? 'zh-TW' : 'zh-CN';
    return new Intl.NumberFormat(locale).format(value);
  }

  private loadBalance(): void {
    const tenantId = String(this.tenant?.id ?? '').trim();
    if (!tenantId) {
      return;
    }

    this.loadingBalance = true;
    this.http
      .post(ConstantUtil.ADMIN_TENANT_V1_POINT_OPERATE_PREPARE, { tenantId })
      .pipe(
        finalize(() => {
          this.loadingBalance = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          const data = res?.data ?? {};
          this.currentBalance = this.toNullableNumber(data?.ailePoint);
          this.preparedTenantName = String(data?.tenantName ?? '').trim();
          this.preparedOpenId = String(data?.openId ?? '').trim();
          this.pointAuthorized = this.toNullableBoolean(data?.authorized);
          this.cdr.markForCheck();
        },
        error: () => {
          this.currentBalance = null;
          this.preparedTenantName = '';
          this.preparedOpenId = '';
          this.pointAuthorized = null;
          this.message.warning(this.t('tenant.point-grant.balance-load-failed'));
        }
      });
  }

  private submitPointGrant(): void {
    const tenantId = String(this.tenant?.id ?? '').trim();
    if (!tenantId) {
      this.message.warning(this.t('tenant.point-grant.validation.invalid'));
      return;
    }

    this.submitting = true;
    this.http
      .post(ConstantUtil.ADMIN_TENANT_V1_POINT_OPERATE_GRANT, this.buildPayload())
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          const success = !!res?.success || res?.status === 0 || res?.status === 200;
          if (!success) {
            this.resultState = 'error';
            this.resultMessage = res?.msg || this.t('tenant.point-grant.result.error-desc');
            this.message.error(this.resultMessage);
            return;
          }

          const data = res?.data ?? {};
          const normalizedStatus = String(data?.status ?? data?.resultStatus ?? '')
            .trim()
            .toLowerCase();
          this.resultState = normalizedStatus === 'processing' || normalizedStatus === 'pending' ? 'processing' : 'success';
          this.resultMessage =
            res?.msg ||
            this.t(
              this.resultState === 'processing' ? 'tenant.point-grant.result.processing-desc' : 'tenant.point-grant.result.success-desc'
            );
          this.resultTraceId = String(data?.outTraceId ?? data?.traceId ?? '').trim();
          this.completed.emit();
        },
        error: err => {
          this.resultState = 'error';
          this.resultMessage = err?.error?.msg || this.t('tenant.point-grant.result.error-desc');
          this.message.error(this.resultMessage);
        }
      });
  }

  private resetFormState(): void {
    this.reasonForm.reset({
      amount: null,
      reason: '',
      remark: ''
    });
    this.currentBalance = null;
    this.preparedTenantName = '';
    this.preparedOpenId = '';
    this.pointAuthorized = null;
    this.resetTransientState();
  }

  private resetTransientState(): void {
    this.submitting = false;
    this.confirmingSubmit = false;
    this.resultState = null;
    this.resultMessage = '';
    this.resultTraceId = '';
  }

  private buildPayload(): Record<string, unknown> {
    const reasonCode = this.normalizeReasonCode(this.reasonForm.controls.reason.value);
    return {
      tenantId: String(this.tenant?.id ?? '').trim(),
      operatorId: this.getOperatorId(),
      point: Number(this.reasonForm.controls.amount.value),
      reasonCode,
      remark: String(this.reasonForm.controls.remark.value ?? '').trim()
    };
  }

  private buildDefaultConfirmSummary(): string {
    return this.t('tenant.point-grant.confirm.content', {
      tenantName: this.tenant?.name || '-',
      amount: Number(this.reasonForm.controls.amount.value)
    });
  }

  private toNullableBoolean(value: unknown): boolean | null {
    if (value === true || value === false) {
      return value;
    }
    return null;
  }

  private toNullableNumber(value: unknown): number | null {
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : null;
  }

  private normalizeReasonCode(value: unknown): string {
    const normalized = String(value ?? '').trim();
    return POINT_GRANT_REASON_CODE_MAP[normalized] ?? normalized;
  }

  private getOperatorId(): string {
    try {
      const loginInfo = JSON.parse(localStorage.getItem('loginInfo') || '{}');
      return String(loginInfo.accountId ?? loginInfo.operatorId ?? loginInfo.userId ?? loginInfo.id ?? '').trim();
    } catch {
      return '';
    }
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.fanyi(key, params);
  }
}
